import type { ControllerRenderProps, FieldPath, FieldValues } from 'react-hook-form';
import { FormFieldWrapper, type FormFieldWrapperProps } from '../components/FormFieldWrapper';
import type { FormFieldSize } from '../helpers/field-size';
import { Autocomplete } from './components/Autocomplete';
import type { AutocompleteOption } from './helpers/types';

/**
 * AutocompleteField コンポーネント
 *
 * FormFieldWrapper でラップされた Autocomplete コンポーネント
 * React Hook Form と統合され、ラベル・バリデーション・エラーメッセージを含む
 *
 * 使用例:
 * ```tsx
 * <AutocompleteField
 *   control={form.control}
 *   name="city"
 *   label="市区町村"
 *   placeholder="入力して検索"
 *   options={[
 *     { label: '東京都', value: 'tokyo' },
 *     { label: '大阪府', value: 'osaka' },
 *   ]}
 *   required
 * />
 * ```
 */
export type AutocompleteFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<FormFieldWrapperProps<TFieldValues, TName>, 'children'> & {
  /** サジェスト候補 */
  options: AutocompleteOption[];
  /** プレースホルダー */
  placeholder?: string;
  /** フィールドの最小幅（デフォルト: sm） */
  size?: FormFieldSize;
  /** 値変更時のコールバック */
  onValueChange?: (value: string) => void;
  /** 無効化フラグ */
  disabled?: boolean;
  /** クリアボタンを表示するか（値が入力されている場合のみ表示） */
  showAllClear?: boolean;
  /** フォーカス時に候補を表示するか（デフォルト: false） */
  showOnFocus?: boolean;
};

export const AutocompleteField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  required,
  supportText,
  showSupportText,
  readOnly,
  displayValue,
  className,
  options,
  placeholder = '入力してください',
  size = 'sm',
  disabled = false,
  onValueChange,
  showAllClear = false,
  showOnFocus = false,
}: AutocompleteFieldProps<TFieldValues, TName>) => {
  return (
    <FormFieldWrapper
      control={control}
      name={name}
      label={label}
      required={required}
      supportText={supportText}
      showSupportText={showSupportText}
      readOnly={readOnly}
      displayValue={displayValue}
      className={className}
      size={size}
    >
      {({
        field,
        fieldId,
        hasError,
      }: {
        field: ControllerRenderProps<TFieldValues, TName>;
        fieldId: string;
        hasError: boolean;
      }) => (
        <Autocomplete
          id={fieldId}
          value={field.value}
          onValueChange={(value: string) => {
            field.onChange(value);
            onValueChange?.(value);
          }}
          onBlur={field.onBlur}
          options={options}
          placeholder={placeholder}
          disabled={disabled}
          showAllClear={showAllClear}
          showOnFocus={showOnFocus}
          hasError={hasError}
        />
      )}
    </FormFieldWrapper>
  );
};

AutocompleteField.displayName = 'AutocompleteField';
